// The CDF data-model-view provider.
//
// CDF is one provider behind the generic `query` source op: it claims
// every view externalId, derives its declared schema from the view via
// `viewToSchema`, and pushes the legal prefix of a query's constraints
// into a native CDF instance-query payload. Whatever it cannot push runs
// client-side through the standard residual op-replay path — identical
// result, the push-down is purely a performance optimisation.
//
// Pushability + native translation are NOT reimplemented here. The eager
// `plan` runs the engine-owned shared plan-time fold (`parseSourcePipeline`)
// with two per-query closures that bind the resolved `DataModelView`:
// `isPushable` delegates to the real CDF recognizer (`isPushableNode`), and
// `translate` is the IDENTITY so the absorbed `native` filter stays raw AST
// (the DMS translation via `translatePredicateToCdf` happens at fetch time,
// where the view is in scope). The engine owns the boolean and/or/not split
// + the ordering-legality gate; CDF owns pushability + native translation.
//
// CDF absorbs the filter (its pushable part), the `columns` wire
// projection, and the `limit` node cap — gated by ordering legality, so a
// `limit` behind a residual filter stays client-side to avoid an
// under-count. It does NOT absorb `sort`: CDF cannot guarantee
// deterministic multi-instance ordering, so sort always re-emits as a
// residual op. Whatever is not absorbed becomes a residual op and replays
// losslessly client-side — the push-down is a pure performance optimisation.
//
// `fetch` rebuilds the CDF instance-query payload and runs it through the
// view's query executor, projecting the response to the declared schema
// via `instancesToColumns`, which mints each per-row timeseries cell
// through the timeseries provider's `buildSource` (`mintSeries`, injected
// at registration) — so a view's nested datapoints cell and a top-level
// `(datapoints)` row cell share one construction path.

import type { QueryRequest } from '@cognite/sdk';

import { type DataModelView, KG_QUERY_MAX_LIMIT, resolveColumnSelector } from '#core/lib/cognite/view.ts';

import type { AstInput } from '../normalize.ts';
import { translatePredicateToCdf } from '../ops/knowledge-graph/pushdown.ts';
import type { ProviderSource } from './types.ts';

/**
 * The provider's absorbed-parameters blob carried on
 * `ProviderSource.native`. Mirrors the deferred CDF instance query: the
 * `view` externalId to read, the pushed predicate (raw AST, translated to
 * the DMS filter at fetch time), the optional wire-level column
 * projection, and the optional node-cap limit. JSON-stable (raw AST +
 * scalars), so it keys the source cache cleanly — distinct pushdowns
 * (different filter / columns / limit) cache distinctly.
 *
 * The pushed predicate is stored as raw AST rather than the translated
 * DMS filter so the cache key is the same predicate shape the engine
 * partitioned, and translation stays a fetch-time concern (it needs the
 * resolved view, which the source descriptor does not carry).
 */
export type CdfViewNative = {
  readonly view: string;
  /** The pushed predicate subtree, raw AST (translated to DMS at fetch time). */
  readonly filter?: AstInput;
  /** Wire-level projection: CDF returns only these properties when set. */
  readonly columns?: readonly string[];
  /** Node-cap for the instance query (defaults to the engine cap at fetch time). */
  readonly limit?: number;
};

/**
 * The default provider `id` the CDF view provider registers under — the
 * dispatch + cache discriminator carried on `ProviderSource.providerId`.
 * Exported as the single source of truth so a consumer that needs to
 * recognise a CDF-rooted source (e.g. result-envelope labelling, recovery
 * context) tests against this constant rather than a re-typed string
 * literal.
 */
export const CDF_VIEW_PROVIDER_ID = 'cdf-view';

/**
 * Sanctioned reader for a CDF view source's absorbed parameters. Returns
 * the `CdfViewNative` blob (view name + pushed filter + columns + limit)
 * when `source` was minted by the CDF view provider, else `null`. The
 * `native` field is otherwise an opaque, provider-private blob; this is
 * the one place outside the provider that is allowed to read its shape,
 * so consumers depend on the typed accessor rather than casting a raw
 * descriptor.
 */
export const cdfViewNativeOf = (source: ProviderSource): CdfViewNative | null =>
  source.providerId === CDF_VIEW_PROVIDER_ID ? (source.native as CdfViewNative) : null;

/**
 * Build the CDF instance-query payload for a pushed-down view query. Folds
 * the pushed filter (translated via `translatePredicateToCdf`) into a
 * top-level `and` so CDF applies it server-side; `columns` controls the
 * wire-level projection (`['*']` for all properties); the node `limit`
 * defaults to the engine cap. Sort is intentionally absent — CDF cannot
 * guarantee deterministic multi-instance sort, so ordering happens
 * client-side via a residual `(sort …)` op.
 *
 * This is the SSOT for the CDF instance-query payload.
 *
 * `resolveInterned` expands any interned identity handle in the pushed filter
 * back to its full `{space, externalId}` vector at translate time; it must come
 * from the SAME session that interned the handle (the provider `fetch` ctx).
 * Absent ⇒ the filter carries no interned handle (a non-interning caller).
 */
export const buildCdfViewQuery = (
  native: CdfViewNative,
  view: DataModelView,
  resolveInterned?: (key: string) => unknown,
): QueryRequest => {
  const filterClauses: readonly unknown[] =
    native.filter === undefined ? [] : [translatePredicateToCdf(native.filter, view, resolveInterned)];
  // Node-level identity keys (`externalId`/`space`) are returned implicitly
  // on every instance and are NOT valid `properties` selections — CDF 400s
  // if listed. Resolve each requested column through the SSOT selector and
  // keep only view-declared properties; node-level columns are projected
  // from the node root by `instancesToColumns` regardless.
  const properties: readonly string[] =
    native.columns === undefined
      ? ['*']
      : native.columns.filter((column: string) => resolveColumnSelector(view, column)[0] !== 'node');
  const query = {
    with: {
      [native.view]: {
        nodes: filterClauses.length > 0 ? { filter: { and: filterClauses } } : {},
        limit: native.limit ?? KG_QUERY_MAX_LIMIT,
      },
    },
    select: {
      [native.view]: {
        sources: [{ source: view, properties }],
      },
    },
  };
  return query as unknown as QueryRequest;
};
